import {
  CloseOutlined,
  CopyOutlined,
  EditOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import { ScopeButton } from '@monorepo/shared-lib';
import { Divider, Flex, Input, Space } from 'antd';
import { StyledCard } from 'components/Custom/card';
import { StyledPopconfirm } from 'components/Custom/Popconfirm';
import { ProviderIcon } from 'components/Custom/ProviderIcon';
import { ProviderLogoNav } from 'components/Custom/ProviderLogoNav';
import { CardTitleSpan } from 'components/Custom/spans';
import { StyledTooltip } from 'components/Custom/Tooltip';
import { CardInfo } from 'components/OrganisationDetails/card-info';
import { MessageStates } from 'containers/MessageBox';
import moment from 'moment';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCountryLabel } from 'hooks/useCountryLabel';
import { useDispatch } from 'react-redux';
import { AccountType, Provider } from 'store/api/enums';
import { turnDrawerOff, turnMessageOn } from 'store/app/actions';
import { MessageData } from 'store/app/types';
import { User } from 'store/auth/models';
import { Country } from 'store/common/models';
import { editOrganisationsRequest } from 'store/organisation/actions';
import {
  EditOrganisationParams,
  Organisation,
} from 'store/organisation/models';
import { getProviderName } from 'utils/get-provider-name';

interface ConnectEntityProps {
  organisation?: Organisation;
  countries?: Country[];
  activeEntity: boolean;
  activateVatChecker: (connectionId: string, entityName: string) => void;
  deactivateVatChecker: (
    connectionId: string,
    entityName: string,
    entityId: string
  ) => void;
  disconnectOrganisation: (organisationId: string) => void;
  currentUser?: User;
}

export const ViesEntityDetails: React.FC<ConnectEntityProps> = ({
  organisation,
  countries,
  activeEntity,
  activateVatChecker,
  deactivateVatChecker,
  disconnectOrganisation,
  currentUser,
}) => {
  const { t } = useTranslation();
  const { localizeCountry } = useCountryLabel();
  //const loggedUser = useSelector(getAuthUser);
  const dispatch = useDispatch();
  const [nameEditMode, setNameEditMode] = useState(false);
  const [nameText, setNameText] = useState(organisation?.friendlyName);

  const onChangeText = (e: any) => setNameText(e.target.value);

  const editModeOn = () => setNameEditMode(true);
  const editModeOff = () => setNameEditMode(false);

  const saveFriendlyName = () => {
    if (nameText) {
      const newName = nameText;
      const newDetails: EditOrganisationParams = {
        newName,
        clientCode: undefined,
        officeLocation: undefined,
        refId: organisation?.uuid ? organisation?.uuid : '',
      };

      dispatch(editOrganisationsRequest(newDetails, true));
      editModeOff();
      organisation = {
        ...organisation,
        friendlyName: nameText,
      } as Organisation;
    } else {
      alert(t('organisation.friendly-name-required'));
    }
  };

  // const onActivateClick = () => {
  //   if(organisation.)
  // }
  const getCountry = (iso: string) => localizeCountry(iso, iso);

  const getWebhookUrl = (uuid: string) => {
    const server = process.env.API_URL.replace('api.', 'papi.')
      .replace('/api', '')
      .replace('papi.', 'api.');
    const url = `${server}/cin7/webhook/${uuid}`;
    return url;
  };

  const copyCurrentValue = (url: string) => {
    navigator.clipboard.writeText(url);

    const message: MessageData = {
      title: t('common.success'),
      description: t('common.copied-to-clipboard'),
      type: MessageStates.INFO,
    };
    dispatch(turnMessageOn(message));
  };

  const onDisconnectOrganisation = (organisationId: string | undefined) => {
    organisationId && disconnectOrganisation(organisationId);
  };

  const renderEntityDetailsCard = () => {
    return (
      <StyledCard //@ts-ignore
        width={'fit-content'}
        style={{ marginTop: '20px' }}
      >
        <div style={{ overflow: 'visible' }}>
          <Space style={{ columnGap: 20 }}>
            <ProviderLogoNav
              providerName={organisation?.providerName}
              shortCode={organisation?.shortCode}
              organisationId={organisation?.uuid}
              style={{ height: '32px', display: 'block' }}
            >
              <ProviderIcon
                providerName={organisation?.providerName}
                alt={organisation?.providerName}
                width={30}
              />
            </ProviderLogoNav>
            <CardTitleSpan>
              {`${
                organisation && getProviderName(organisation?.providerName)
              } ${t('vat-checker.details')}`}
            </CardTitleSpan>
          </Space>
          <Divider style={{ margin: '16px 0' }} />

          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              marginBottom: '4px',
              flexDirection: 'column',
              rowGap: 5,
              overflow: 'auto',
              maxWidth: 1000,
              width: '100%' /*minWidth: 916.6*/,
            }}
          >
            {organisation?.legalName && (
              <CardInfo
                label={t('signup.company-name')}
                data={organisation?.legalName}
              />
            )}

            {organisation?.friendlyName &&
              currentUser &&
              AccountType[currentUser.accountType] !== AccountType.CLIENT && (
                <Flex justify="space-between" align="end">
                  <CardInfo
                    label={t('organisation.friendly-name')}
                    data={
                      nameEditMode ? (
                        <Input value={nameText} onChange={onChangeText} />
                      ) : (
                        //organisation?.friendlyName
                        nameText
                      )
                    }
                  />

                  <div
                    className="edit-details-link"
                    style={{ paddingTop: '10px' }}
                    // onClick={() =>
                    //   nameEditMode ? saveFriendlyName() : editModeOn()
                    // }
                  >
                    {nameEditMode ? (
                      <Space>
                        <SaveOutlined
                          role="button"
                          tabIndex={0}
                          aria-label={t('common.save')}
                          onClick={saveFriendlyName}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              saveFriendlyName();
                            }
                          }}
                        />
                        <CloseOutlined
                          role="button"
                          tabIndex={0}
                          aria-label={t('common.close')}
                          onClick={editModeOff}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              editModeOff();
                            }
                          }}
                        />
                      </Space>
                    ) : (
                      <EditOutlined
                        role="button"
                        tabIndex={0}
                        aria-label={t('common.edit')}
                        onClick={editModeOn}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            editModeOn();
                          }
                        }}
                      />
                    )}
                  </div>
                </Flex>
              )}

            {organisation?.creator && (
              <CardInfo
                label={t('organisation.created-by')}
                data={organisation?.creator}
              />
            )}

            {organisation?.createTimestamp && (
              <CardInfo
                label={t('organisation.created-on')}
                data={moment(organisation?.createTimestamp).format(
                  'DD/MM/YYYY'
                )}
              />
            )}

            {organisation?.currency && (
              <CardInfo
                label={t('common.currency')}
                data={organisation?.currency}
              />
            )}

            {organisation?.taxNumber && (
              <CardInfo
                label={t('organisation.tax-number')}
                data={organisation?.taxNumber}
              />
            )}

            {organisation?.country && (
              <CardInfo
                label={t('common.country')}
                data={getCountry(organisation?.country)}
              />
            )}

            {organisation?.providerName === Provider.CIN7 && (
              <Flex justify="space-between" align="end">
                <CardInfo label={t('vat-checker.webhook-url')} data="" />
                <div
                  className="edit-details-link"
                  style={{ paddingTop: '10px' }}
                  // onClick={() =>
                  //   nameEditMode ? saveFriendlyName() : editModeOn()
                  // }
                >
                  {' '}
                  <StyledTooltip //@ts-ignore
                    title={
                      <>
                        <strong>{t('vat-checker.click-to-copy')}</strong>
                        <br />
                        {getWebhookUrl(organisation?.uuid)}
                      </>
                    }
                    placement="left"
                  >
                    <CopyOutlined
                      onClick={() => {
                        organisation &&
                          copyCurrentValue(getWebhookUrl(organisation?.uuid));
                        dispatch(turnDrawerOff());
                      }}
                    />
                  </StyledTooltip>
                </div>
              </Flex>
            )}

            <StyledPopconfirm //@ts-ignore
              title={
                activeEntity
                  ? t('vat-checker.deactivating-entity-title')
                  : t('vat-checker.activating-entity-title')
              }
              description={
                activeEntity
                  ? t('vat-checker.deactivate-entity-description')
                  : t('vat-checker.activate-entity-description')
              }
              onConfirm={() => {
                if (organisation) {
                  activeEntity
                    ? deactivateVatChecker(
                        organisation?.connectionId,
                        organisation?.friendlyName,
                        organisation?.uuid
                      )
                    : activateVatChecker(
                        organisation?.connectionId,
                        organisation?.friendlyName
                      );
                }
                dispatch(turnDrawerOff());
              }}
              placement="left"
              okText={t('common.yes')}
              cancelText={t('common.cancel')}
            >
              <ScopeButton
                type="default" //@ts-ignore
                width="206px"
                height="32px"
                style={{ margin: '0 30px', marginTop: '12px' }}
                /* disabled={
                  currentUser &&
                  AccountType[currentUser?.accountType] === AccountType.CLIENT
                } */
              >
                {activeEntity ? t('vat-checker.deactivate') : t('billing.activate')}
              </ScopeButton>
            </StyledPopconfirm>
            <StyledPopconfirm //@ts-ignore
              title={
                organisation?.connectedProperly
                  ? t('organisation.disconnected-entity-title')
                  : t('organisation.removed-entity-title')
              }
              description={
                <span>
                  {organisation?.connectedProperly
                    ? t('organisation.disconnected-entity-description')
                    : t('organisation.removed-entity-description')}
                </span>
              }
              onConfirm={() => {
                onDisconnectOrganisation(organisation?.uuid);
                dispatch(turnDrawerOff());
              }}
              placement="left"
              okText={t('common.yes')}
              cancelText={t('common.cancel')}
            >
              <ScopeButton
                type="primary" //@ts-ignore
                width="206px"
                height="32px"
                style={{ margin: '0 30px', marginTop: '12px' }}
                disabled={
                  currentUser &&
                  AccountType[currentUser?.accountType] === AccountType.CLIENT
                }
              >
                {organisation?.connectedProperly
                  ? t('common.disconnect')
                  : t('common.remove')}
              </ScopeButton>
            </StyledPopconfirm>
          </div>
        </div>
      </StyledCard>
    );
  };
  return <div>{renderEntityDetailsCard()}</div>;
};

